// Authorize.Net Dashboard — thin config over the shared ReconDashboard shell. All layout/behavior lives
// in ReconDashboard; this file supplies only Authorize.Net's data (endpoints, account handling, tiles).
import React from 'react';
import ReconDashboard from '../../components/recon/ReconDashboard';
import { buildAuthnetNav } from '../../config/recon/authnetNav';
import { fmtMoney, fmtLastSynced } from '../../components/recon/reconShared';
import { get } from '../../utils/api';

export const authnetDashConfig = {
  provider: 'authnet',
  title: 'Dashboard',
  accounts: true,
  syncTitle: 'Sync now — pull fresh Authorize.Net data for this account',
  buildNav: (active, account, navigate) => buildAuthnetNav(active, account, navigate),

  // One load() per (account) change. Recon is per-account; the aux lists (subscriptions, maps,
  // failures) are account-independent and filtered per-account in sections(). Each aux call is caught
  // independently so a failing subscriptions endpoint degrades to "—" instead of blanking the page.
  load: async (account) => {
    const recon = await get('/api/manage/authnet-reconcile', { year: 'all', month: 'all', account });
    let subs; try { subs = await get('/api/manage/authnet-subscriptions'); }
    catch (se) { subs = { error: se?.response?.data?.error || se?.message || 'Could not load subscriptions' }; }
    let maps = {};
    try { const mm = await get('/api/manage/authnet-subscriptions/maps'); (mm?.mappings || []).forEach((x) => { maps[`${x.account}:${x.subscription_id}`] = x; }); } catch { /* non-fatal */ }
    let failures = { by_sub: {}, total: 0, subs_with_failures: 0, last_run: null };
    try { const f = await get('/api/manage/authnet-subscription-failures'); if (f) failures = f; } catch { /* non-fatal */ }
    return { recon, subs, maps, failures };
  },
  lastSynced: (d) => fmtLastSynced(d?.recon?.last_run),
  accountsList: (d) => d?.recon?.accounts || d?.subs?.accounts || [],

  sections: ({ data, account, navigate }) => {
    const recon = data?.recon || {};
    const subs = data?.subs; const maps = data?.maps || {};
    const failures = data?.failures || {};
    const counts = recon.counts || {}; const amounts = recon.amounts || {};
    const subErr = subs?.error;

    const subList = (Array.isArray(subs?.subscriptions) ? subs.subscriptions : []).filter((s) => account === 'all' || s.account === account);
    const mappedCount = subList.filter((s) => maps[`${s.account}:${s.id}`]).length;
    const toMap = Math.max(0, subList.length - mappedCount);
    const activeCount = subList.filter((s) => String(s.status).toLowerCase() === 'active').length;
    const expiredList = subList.filter((s) => /expir/.test(String(s.status || '').toLowerCase()));
    const expiredReview = expiredList.filter((s) => {
      const total = s.total_occurrences == null ? null : Number(s.total_occurrences);
      const paid = Number(s.stats?.paid_count ?? 0);
      const problems = Number(s.stats?.problem_count || 0);
      return !(total != null && Math.max(0, total - paid) === 0 && problems === 0);
    }).length;
    const monthlyRecurring = subList.filter((s) => String(s.status).toLowerCase() === 'active').reduce((a, s) => a + (Number(s.amount) || 0), 0);
    let failSubs; let failAttempts;
    if (account === 'all') { failSubs = failures?.subs_with_failures || 0; failAttempts = failures?.total || 0; }
    else {
      failSubs = 0; failAttempts = 0;
      Object.entries(failures?.by_sub || {}).forEach(([k, n]) => { if (k.startsWith(`${account}:`) && Number(n) > 0) { failSubs += 1; failAttempts += Number(n) || 0; } });
    }

    const acctQ = account && account !== 'all' ? `&acct=${account}` : '';
    const goSubs = (view, status) => navigate(`/payments/authnet/subscriptions?view=${view}${status ? `&status=${status}` : ''}${acctQ}`);
    const goRecon = (tab) => navigate(`/payments/authnet/reconciliation?tab=${tab}&year=all&month=all${acctQ}`);
    const v = (x) => (subErr ? '—' : x);
    const click = (fn) => (subErr ? undefined : fn);

    return [
      {
        label: 'Recurring (subscriptions)',
        note: subErr ? `Subscriptions list: ${subErr}` : null,
        tiles: [
          { label: 'Subscriptions', value: v(subList.length), sub: 'all statuses · click to clear', color: 'primary', onClick: click(() => goSubs('all', 'all')) },
          { label: 'In progress', value: v(activeCount), sub: 'recurring now', color: 'success', onClick: click(() => goSubs('all', 'active')) },
          { label: 'Mapped', value: v(mappedCount), sub: 'devotee + allocation', color: 'success', onClick: click(() => goSubs('mapped')) },
          { label: 'Unmapped', value: v(toMap), sub: 'need setup', color: 'warning', onClick: click(() => goSubs('unmapped')) },
          { label: 'Failed charges', value: v(failSubs), sub: `${failAttempts} attempts · click`, color: 'error', onClick: click(() => goSubs('failed')) },
          { label: 'Expired', value: v(expiredList.length), sub: expiredReview > 0 ? `${expiredReview} need review · click` : 'terms complete · click', color: expiredReview > 0 ? 'warning' : null, onClick: click(() => goSubs('all', 'expired')) },
          { label: 'Monthly recurring', value: v(fmtMoney(monthlyRecurring)), sub: 'sum of active', color: null },
        ],
      },
      {
        label: 'Transaction reconciliation',
        tiles: [
          { label: 'Authorize.Net payments', value: recon.total ?? '—', sub: 'in this period · click for all', color: null, onClick: () => goRecon('all') },
          { label: 'Order', value: (counts.matched ?? 0) + (counts.needs_link ?? 0), sub: 'has an order', color: 'success', onClick: () => goRecon('order') },
          { label: 'No order · at risk', value: counts.unbooked ?? 0, sub: fmtMoney(amounts.unbooked), color: 'error', onClick: () => goRecon('no_order') },
          { label: 'Returned (ACH came back)', value: counts.returned ?? 0, sub: fmtMoney(amounts.returned), color: 'warning', onClick: () => goRecon('returned') },
        ],
      },
    ];
  },
};

export default function AuthnetDashboardNewPage() {
  return <ReconDashboard config={authnetDashConfig} />;
}
